import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Autocomplete, {
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import { EntityAutocompletePickerInput } from '../EntityAutocompletePickerInput';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EntityAutocompletePickerOption } from '../EntityAutocompletePickerOption';
import {
  catalogApiRef,
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { TextFieldProps } from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { reduceBackendCatalogFilters } from '../utils/filters';
import Chip from '@mui/material/Chip';
import { isEqual } from 'lodash';
import { useClearEntityFilters } from '@mb.io/core-components';

export type AllowedEntityFilters<T extends DefaultEntityFilters> = {
  [K in keyof T]-?: NonNullable<T[K]> extends EntityFilter & {
    values: string[];
  }
    ? K
    : never;
}[keyof T];

export type EntityAutocompletePickerProps<
  T extends DefaultEntityFilters = DefaultEntityFilters,
  Name extends AllowedEntityFilters<T> = AllowedEntityFilters<T>,
> = {
  label: string;
  path: string;
  showCounts?: boolean;
  Filter: { new (values: string[]): NonNullable<T[Name]> };
  InputProps?: TextFieldProps;
  initialSelectedOptions?: string[];
  filtersForAvailableValues?: Array<keyof T>;
};

const useStyles = makeStyles(
  theme => ({
    root: {},
    label: {
      textTransform: 'none',
      fontWeight: 'bold',
    },
    common: {
      width: '100%',
      borderRadius: '2px',
      border: 'none',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.12)'
          : 'rgba(0, 0, 0, 0.08)',
    },
    select: {
      borderBottom:
        theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.70)'
          : '1px solid rgba(0, 0, 0, 0.60)',
    },
    selectDisabled: {
      borderBottom: 'none',
    },
    autocomplete: {
      '&:hover': {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.16)'
            : 'rgba(0, 0, 0, 0.10)',
      },
    },
  }),
  { name: 'CatalogReactEntityAutocompletePicker' },
);

export function EntityAutocompletePicker<
  T extends DefaultEntityFilters = DefaultEntityFilters,
  Name extends AllowedEntityFilters<T> = AllowedEntityFilters<T>,
>(props: Readonly<EntityAutocompletePickerProps<T, Name>>) {
  const {
    path,
    showCounts,
    Filter,
    InputProps,
    initialSelectedOptions = [],
    filtersForAvailableValues = ['kind'],
  } = props;

  const classes = useStyles();

  const { clearFilterState, resetFilters } = useClearEntityFilters();

  const [useQueryParameters, setUseQueryParameters] = useState<boolean>(true);

  const {
    updateFilters,
    filters,
    queryParameters: { tags: queryParameter },
  } = useEntityList<T>();

  const catalogApi = useApi(catalogApiRef);
  const availableValuesFilters = filtersForAvailableValues.map(
    f => filters[f] as EntityFilter | undefined,
  );
  const { value: availableValues } = useAsync(async () => {
    const facet = path;
    const { facets } = await catalogApi.getEntityFacets({
      facets: [facet],
      filter: reduceBackendCatalogFilters(
        availableValuesFilters.filter(Boolean) as EntityFilter[],
      ),
    });

    return Object.fromEntries(
      facets[facet].map(({ value, count }) => [value, count]),
    );
  }, [...availableValuesFilters]);

  const queryParameters = useMemo(
    () => [queryParameter].flat().filter(Boolean) as string[],
    [queryParameter],
  );

  const [selectedOptions, setSelectedOptions] = useState(
    queryParameters.length
      ? queryParameters
      : ((filters.tags as unknown as { values: string[] })?.values ??
          initialSelectedOptions),
  );

  // Set selected options on query parameter updates; this happens at initial page load and from
  // external updates to the page location
  useEffect(() => {
    if (queryParameters.length) {
      setSelectedOptions(queryParameters);
      updateFilters({
        tags: new Filter(queryParameters),
      } as unknown as Partial<T>);
    }
  }, [Filter, queryParameters, updateFilters]);

  const [cachedAvailableOptions, setCachedAvailableOptions] = useState<
    string[]
  >([]);
  const availableOptions = Object.keys(availableValues ?? {});

  const filter = filters.tags;
  const isDisabled =
    (filter && typeof filter === 'object' && !('values' in filter)) ||
    !availableOptions.length;

  useEffect(() => {
    if (clearFilterState.tags) {
      setUseQueryParameters(false);
      setSelectedOptions([]);
      updateFilters({
        tags: undefined,
      } as Partial<T>);
    }
  }, [clearFilterState, updateFilters]);

  const hasChanged = useMemo(() => {
    return !isEqual(availableOptions, cachedAvailableOptions);
  }, [availableOptions, cachedAvailableOptions]);

  useEffect(() => {
    // When the kind filter changes, available options also change
    if (hasChanged) {
      setCachedAvailableOptions(availableOptions);
      let currentOptions: string[] = [];

      const isQueryParametersValid = queryParameters.every(param =>
        availableOptions.includes(param),
      );
      if (isQueryParametersValid && useQueryParameters) {
        currentOptions = queryParameters;
      } else {
        // Reset the selected options
        currentOptions = [];
      }

      setSelectedOptions(currentOptions);
      updateFilters({
        tags: currentOptions.length ? new Filter(currentOptions) : undefined,
      } as Partial<T>);
    }
  }, [
    hasChanged,
    availableOptions,
    queryParameters,
    useQueryParameters,
    updateFilters,
    Filter,
  ]);

  return (
    <Box className={classes.root}>
      <Autocomplete<string, true>
        multiple
        limitTags={1}
        disableCloseOnSelect
        options={cachedAvailableOptions}
        value={selectedOptions}
        popupIcon={<ExpandMoreIcon />}
        className={!isDisabled ? classes.autocomplete : ''}
        onChange={(_event: object, options: string[]) => {
          setSelectedOptions(options);
          setUseQueryParameters(false);
          const shouldUpdateFilter = options.length && availableOptions.length;
          updateFilters({
            tags: shouldUpdateFilter ? new Filter(options) : undefined,
          } as Partial<T>);
          resetFilters('tags');
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip label={option} size="small" {...getTagProps({ index })} />
          ))
        }
        renderOption={(
          liProps,
          option,
          state: AutocompleteRenderOptionState,
        ) => (
          <li {...liProps}>
            <EntityAutocompletePickerOption
              selected={state.selected}
              value={option}
              availableOptions={availableValues}
              showCounts={!!showCounts}
            />
          </li>
        )}
        renderInput={params => (
          <EntityAutocompletePickerInput
            {...params}
            {...InputProps}
            className={`${classes.common} ${isDisabled ? classes.selectDisabled : classes.select}`}
            variant="standard"
          />
        )}
        disabled={isDisabled}
      />
    </Box>
  );
}
